import { useCallback, useEffect, useMemo, useState } from 'react';
import './mockTasks';

import { useNewApiCall } from 'hooks';
import moment from 'moment';
import { shiftToDaySchedule } from 'utils/calendarUtils';

import {
  AbsenceService,
  DepartmentsService,
  ScheduleDepartmentService,
  SchedulePersonShiftService
} from '../../components/services';
import {
  CalendarAssignment,
  DaySchedule,
  DepartmentSchedules,
  EmployeesSchedules,
  PersonAbsence,
  Shift,
  TaskForCalendarAssignment,
  TimelineScale,
  TreeNode
} from '../../types/interfaces';
import { findNodeByKey, generateGUID } from '../../utils';

import { mockGetTasksByDepartment } from './mockTasks';
import { validateShifts } from './shiftValidation';

type DeletedAbsenceKey = { id: number } | { guid: string };

interface UseDataManagerProps {
  selectedDepartmentFilter: TreeNode | null;
  highlightedDateForView: moment.Moment | null;
  baseDate: moment.Moment;
  selectedDateFilter: Date | null;
  timelineScale: TimelineScale;
  dFilter: any;
  toast: any;
  apiParams: {
    date: string | undefined;
    name: string | undefined;
    personnelNumber: string | undefined;
    sortBy: 'personnelNumber' | 'name' | undefined;
    sortDirection: 'asc' | 'desc' | undefined;
  };
  setTimelineVersion: React.Dispatch<React.SetStateAction<number>>;
  setSelectedDepartmentFilter: React.Dispatch<
    React.SetStateAction<TreeNode | null>
  >;
  viewMode: 'schedule' | 'timesheet';
  showOnlyFreeEmployees?: boolean;
  excludeLongAbsences?: boolean;
  todayShiftOnly?: boolean;
}

export const useDataManager = (props: UseDataManagerProps) => {
  const {
    dFilter,
    apiParams,
    selectedDepartmentFilter,
    selectedDateFilter,
    timelineScale,
    toast,
    setTimelineVersion,
    setSelectedDepartmentFilter,
    viewMode,
    highlightedDateForView,
    baseDate,
    showOnlyFreeEmployees = false,
    excludeLongAbsences = false,
    todayShiftOnly = false
  } = props;

  const [calendarAssignment, setCalendarAssignment] =
    useState<CalendarAssignment | null>(null);
  const [departmentSchedules, setDepartmentSchedules] =
    useState<DepartmentSchedules | null>(null);
  const [employeesTimesheet, setEmployeesTimesheet] = useState<
    Record<string, Record<string, number>>
  >({});
  const [employeesTotalTimesheet, setEmployeesTotalTimesheet] = useState<
    Record<string, number>
  >({});
  const [employeesShifts, setEmployeesShifts] =
    useState<EmployeesSchedules | null>(null);
  const [employeesAbsences, setEmployeesAbsences] = useState<
    Record<string, Record<string, PersonAbsence[]>>
  >({});
  const [employeesTasks, setEmployeesTasks] = useState<
    Record<string, TaskForCalendarAssignment[]>
  >({});
  const [newAbsences, setNewAbsences] = useState<PersonAbsence[]>([]);
  const [updatedAbsences, setUpdatedAbsences] = useState<PersonAbsence[]>([]);
  const [deletedAbsenceKeys, setDeletedAbsenceKeys] = useState<
    DeletedAbsenceKey[]
  >([]);
  const [newShifts, setNewShifts] = useState<Shift[]>([]);
  const [updatedShifts, setUpdatedShifts] = useState<Shift[]>([]);
  const [deletedShiftIds, setDeletedShiftIds] = useState<string[]>([]);
  const [simulatedDeletedShifts, setSimulatedDeletedShifts] = useState<
    string[]
  >([]);
  const [simulatedUpdatedShifts, setSimulatedUpdatedShifts] = useState<Shift[]>(
    []
  );
  const [simulatedChangesByAbsence, setSimulatedChangesByAbsence] = useState<
    Record<string, { deleted: string[]; updated: string[] }>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [isDepartmentsLoaded, setIsDepartmentsLoaded] = useState(false);
  const [treeDropDownValue, setTreeDropDownValue] = useState<TreeNode[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const dateForQuery = selectedDateFilter
    ? moment(selectedDateFilter).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');

  const effectiveMomentForTimesheet = useMemo(() => {
    let effectiveMoment: moment.Moment;
    if (timelineScale === TimelineScale.MONTH) {
      effectiveMoment = baseDate.clone().startOf('month');
    } else if (timelineScale === TimelineScale.WEEK) {
      const date = highlightedDateForView || baseDate;
      effectiveMoment = date.clone().startOf('isoWeek');
    } else {
      const date = highlightedDateForView || baseDate;
      effectiveMoment = date.clone().startOf('day');
    }
    return effectiveMoment;
  }, [timelineScale, baseDate, highlightedDateForView]);

  const dateForTotalTimesheetQuery = useMemo(() => {
    const vtsMoment = effectiveMomentForTimesheet;
    const selectedMonthStart = baseDate.clone().startOf('month');

    if (
      timelineScale === TimelineScale.WEEK &&
      vtsMoment.isBefore(selectedMonthStart)
    ) {
      return selectedMonthStart.format('YYYY-MM-DD');
    }

    return vtsMoment.format('YYYY-MM-DD');
  }, [effectiveMomentForTimesheet, baseDate, timelineScale]);

  const fetchTimesheet = useCallback(
    async (signal?: AbortSignal) => {
      if (!selectedDepartmentFilter?.key) return;
      try {
        const departmentId = Number(selectedDepartmentFilter.key);
        const response = await SchedulePersonShiftService.getPersonsTimesheet(
          dateForQuery,
          departmentId
        );
        if (signal?.aborted) return;
        setEmployeesTimesheet(response.data.value || {});
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load timesheet'
        });
      }
    },
    [selectedDepartmentFilter, dateForQuery, toast]
  );

  const fetchTotalTimesheet = useCallback(async () => {
    if (!selectedDepartmentFilter?.key) return;
    try {
      const departmentId = Number(selectedDepartmentFilter.key);
      const response = await SchedulePersonShiftService.getTotalTimesheet(
        dateForTotalTimesheetQuery,
        departmentId,
        timelineScale === TimelineScale.WEEK
      );
      setEmployeesTotalTimesheet(response.data.value || {});
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load timesheet'
      });
    }
  }, [
    selectedDepartmentFilter,
    dateForTotalTimesheetQuery,
    timelineScale,
    toast
  ]);

  useNewApiCall(fetchTotalTimesheet, [
    selectedDepartmentFilter,
    timelineScale,
    dateForTotalTimesheetQuery,
    viewMode
  ]);

  // Оригинальная логика getDepartmentsCallback
  const getDepartmentsCallback = useCallback(async () => {
    try {
      setIsDepartmentsLoaded(false);
      const result = await DepartmentsService.getDepartments();

      const departmentsArray = result.data;

      if (!Array.isArray(departmentsArray)) {
        console.error('Некорректный формат данных подразделений:', result.data);
        toast.current?.show({
          severity: 'error',
          detail: 'Некорректный формат данных подразделений'
        });
        return;
      }

      setTreeDropDownValue([...departmentsArray]);

      if (dFilter) {
        try {
          const storedFilter = JSON.parse(dFilter) as TreeNode;
          const parsedDFilter = findNodeByKey(
            departmentsArray,
            storedFilter?.key
          );

          const areFiltersEqual =
            !!parsedDFilter &&
            parsedDFilter.key === storedFilter.key &&
            parsedDFilter.label === storedFilter.label;

          if (!areFiltersEqual && parsedDFilter) {
            localStorage.setItem(
              'storedDepartmentFilter',
              JSON.stringify(parsedDFilter)
            );
          }

          setSelectedDepartmentFilter(parsedDFilter);

          if (!parsedDFilter || parsedDFilter.key === '') {
            toast.current?.show({
              severity: 'warn',
              summary: 'Предупреждение',
              detail: 'Выберите участок для отображения данных в таблице',
              life: 5000
            });
          }
        } catch (err) {
          console.error('Ошибка при чтении storedDepartmentFilter:', err);
          toast.current?.show({
            severity: 'warn',
            summary: 'Внимание',
            detail: 'Фильтр участков был повреждён, выберите участок вручную.',
            life: 5000
          });
          setSelectedDepartmentFilter(null);
        }
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'Предупреждение',
          detail: 'Выберите участок для отображения данных в таблице',
          life: 5000
        });
      }
    } catch (error) {
      console.error('Ошибка загрузки участков:', error);
      toast.current?.show({
        severity: 'error',
        detail: 'Не удалось загрузить участки'
      });
    } finally {
      setIsDepartmentsLoaded(true);
    }
  }, [dFilter]);

  // Оригинальная логика fetchPersonAbsences
  const fetchPersonAbsences = useCallback(
    async (signal?: AbortSignal) => {
      if (!selectedDepartmentFilter?.key) return;
      try {
        const departmentId = Number(selectedDepartmentFilter.key);
        const response = await AbsenceService.getPersonsAbsences(
          departmentId,
          dateForQuery
        );
        if (signal?.aborted) return;

        const absencesData = response.data?.value || {};
        const absencesByPerson: Record<
          string,
          Record<string, PersonAbsence[]>
        > = {};

        Object.entries(absencesData).forEach(([date, absences]) => {
          absences.forEach((absence) => {
            absence.guid = generateGUID();
            const personIdStr = absence.personId.toString();
            if (!absencesByPerson[personIdStr]) {
              absencesByPerson[personIdStr] = {};
            }
            if (!absencesByPerson[personIdStr][date]) {
              absencesByPerson[personIdStr][date] = [];
            }
            absencesByPerson[personIdStr][date].push(absence);
          });
        });

        setEmployeesAbsences(absencesByPerson);
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load absences'
        });
      }
    },
    [selectedDepartmentFilter, dateForQuery]
  );

  const fetchPersonTasks = useCallback(
    async (signal?: AbortSignal) => {
      if (!selectedDepartmentFilter?.key) return;
      try {
        const departmentId = Number(selectedDepartmentFilter.key);
        const response = await DepartmentsService.getTasksByDepartment(
          departmentId,
          dateForQuery
        );
        if (signal?.aborted) return;
        const tasksData = response.data?.value || {};

        const tasksByPerson: Record<string, TaskForCalendarAssignment[]> = {};

        Object.entries(tasksData).forEach(([personId, tasks]) => {
          tasksByPerson[personId] = tasks as TaskForCalendarAssignment[];
        });

        setEmployeesTasks(tasksByPerson);
      } catch (error) {
        console.log('error: ', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Ошибка загруки нарядов'
        });
      }
    },
    [selectedDepartmentFilter, dateForQuery]
  );

  // Оригинальные функции валидации и handlers (идентично оригиналу)
  function getNewShiftInterval(shift: Shift) {
    const start = moment(`${shift.date}T${shift.workdayStart}:00`);
    const [hours, minutes] = shift.workdayDuration.split(':').map(Number);
    const end = start.clone().add({ hours, minutes });
    return { start, end };
  }

  function isShiftOverlapping(
    newShift: Shift,
    allShifts: Shift[],
    deletedShiftIds: string[]
  ): boolean {
    const newInterval = getNewShiftInterval(newShift);
    const previousDay = moment(newShift.date)
      .subtract(1, 'days')
      .format('YYYY-MM-DD');
    const nextDay = moment(newShift.date).add(1, 'days').format('YYYY-MM-DD');
    const relevantShifts = allShifts.filter(
      (s) =>
        s.personId === newShift.personId &&
        [newShift.date, previousDay, nextDay].includes(s.date) &&
        !deletedShiftIds.includes(s.guid) &&
        s.guid !== newShift.guid
    );
    for (const shift of relevantShifts) {
      const existingInterval = getNewShiftInterval(shift);
      if (
        newInterval.start.isBefore(existingInterval.end) &&
        newInterval.end.isAfter(existingInterval.start)
      ) {
        return true;
      }
    }
    return false;
  }

  const isAbsenceOverlapping = (
    newAbsence: PersonAbsence,
    allAbsences: PersonAbsence[]
  ): boolean => {
    const newStart = moment(newAbsence.from);
    const newEnd = moment(newAbsence.to);
    return allAbsences.some((abs) => {
      const isSameRecord =
        (abs.id && newAbsence.id && abs.id === newAbsence.id) ||
        (abs.guid && newAbsence.guid && abs.guid === newAbsence.guid);
      if (isSameRecord) return false;
      if (abs.personId !== newAbsence.personId) return false;
      const absStart = moment(abs.from);
      const absEnd = moment(abs.to);
      return newStart.isBefore(absEnd) && newEnd.isAfter(absStart);
    });
  };

  const getOverlappingShifts = (
    absence: PersonAbsence,
    allShifts: Shift[]
  ): Shift[] => {
    const absStart = moment(absence.from);
    const absEnd = moment(absence.to);
    return allShifts.filter((shift) => {
      const shiftStart = moment(`${shift.date}T${shift.workdayStart}:00`);
      const [h, m] = shift.workdayDuration.split(':').map(Number);
      const shiftEnd = shiftStart.clone().add({ hours: h, minutes: m });
      return (
        shift.personId === absence.personId &&
        absStart.isBefore(shiftEnd) &&
        absEnd.isAfter(shiftStart)
      );
    });
  };

  const getOriginalShiftData = (
    guid: string
  ): { personId: number; date: string; id?: number } | null => {
    if (!employeesShifts) return null;

    for (const [personIdStr, schedulesByDate] of Object.entries(
      employeesShifts
    )) {
      for (const [date, shifts] of Object.entries(schedulesByDate)) {
        const shift = shifts.find((s) => s.guid === guid);
        if (shift) {
          return {
            personId: Number(personIdStr),
            date,
            id: shift.id
          };
        }
      }
    }

    // Проверка новых и обновленных смен
    const newOrUpdatedShift = [...newShifts, ...updatedShifts].find(
      (s) => s.guid === guid
    );
    if (newOrUpdatedShift) {
      return {
        personId: newOrUpdatedShift.personId,
        date: newOrUpdatedShift.date,
        id: newOrUpdatedShift.id
      };
    }

    return null;
  };

  function moveShiftInEmployeesShifts(
    employeesShifts: EmployeesSchedules, // твой тип
    shift: Shift,
    oldPersonId: number,
    oldDate: string
  ): EmployeesSchedules {
    // Клонируем объект (чтобы не мутировать state напрямую)
    const newEmployeesShifts = { ...employeesShifts };
    // Удаляем shift у старого сотрудника/даты
    if (newEmployeesShifts[oldPersonId]?.[oldDate]) {
      newEmployeesShifts[oldPersonId][oldDate] = newEmployeesShifts[
        oldPersonId
      ][oldDate].filter((s) => s.guid !== shift.guid);
      // Если после удаления дата пуста — удаляем ключ
      if (newEmployeesShifts[oldPersonId][oldDate].length === 0) {
        delete newEmployeesShifts[oldPersonId][oldDate];
      }
    }
    // Добавляем/обновляем shift у нового сотрудника/даты
    const personIdStr = shift.personId.toString();
    if (!newEmployeesShifts[personIdStr]) newEmployeesShifts[personIdStr] = {};
    if (!newEmployeesShifts[personIdStr][shift.date])
      newEmployeesShifts[personIdStr][shift.date] = [];
    // Удаляем на всякий случай по guid
    newEmployeesShifts[personIdStr][shift.date] = newEmployeesShifts[
      personIdStr
    ][shift.date].filter((s) => s.guid !== shift.guid);
    newEmployeesShifts[personIdStr][shift.date].push(shiftToDaySchedule(shift));
    return newEmployeesShifts;
  }

  // Handlers для shifts
  const handleSaveShift = useCallback(
    async (shift: Shift) => {
      const duration = moment.duration(shift.workdayDuration);
      if (duration.asHours() > 24) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Смена не может превышать 24 часа'
        });
        return false;
      }

      const original = getOriginalShiftData(shift.guid);
      if (
        original &&
        (original.personId !== shift.personId || original.date !== shift.date)
      ) {
        setEmployeesShifts((prev) =>
          moveShiftInEmployeesShifts(
            prev!,
            shift,
            original.personId,
            original.date
          )
        );
      }

      // Собираем все смены для валидации (включая текущую)
      const cleanShift = {
        ...shift,
        isTemplate: false,
        guid: shift.guid.startsWith('temp-') ? generateGUID() : shift.guid
      };

      const allShiftsForValidation = [
        ...(calendarAssignment?.employees.flatMap((emp) =>
          Object.entries(employeesShifts?.[emp.id.toString()] ?? {}).flatMap(
            ([date, shiftArray]) =>
              shiftArray
                .map((s) => ({
                  ...s,
                  personId: emp.id,
                  date,
                  guid: s.guid,
                  breakDuration: s.break
                }))
                .filter((s) => s.guid !== shift.guid) // Исключаем старую версию смены
          )
        ) ?? []),
        ...newShifts.filter((s) => s.guid !== shift.guid),
        ...updatedShifts.filter((s) => s.guid !== shift.guid),
        ...simulatedUpdatedShifts.filter((s) => s.guid !== shift.guid),
        cleanShift // Добавляем новую/обновленную смену
      ].filter(
        (s) =>
          !deletedShiftIds.includes(s.guid) &&
          !simulatedDeletedShifts.includes(s.guid)
      );

      if (
        isShiftOverlapping(
          cleanShift,
          allShiftsForValidation as Shift[],
          deletedShiftIds
        )
      ) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Смена пересекается с существующей'
        });
        return false;
      }

      // Проверяем ограничения по количеству смен/часов
      const monthStart = baseDate.clone().startOf('month');
      const validationErrors = await validateShifts(
        cleanShift.personId,
        allShiftsForValidation as Shift[],
        monthStart,
        calendarAssignment?.employees
      );
      console.log('validateShifts: ', validationErrors);

      if (validationErrors.length > 0) {
        const errorMessages = validationErrors.map(
          (err) =>
            `По сотруднику ${
              err.employeeName || err.personId
            } нарушается правило: ${err.ruleName}`
        );
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка валидации',
          detail: errorMessages.join('; '),
          life: 5000
        });
        return false;
      }

      const newShift = newShifts.find((s) => s.guid === shift.guid);

      if (shift.guid.startsWith('temp-') && !newShift) {
        setNewShifts((prev) => [...prev, cleanShift]);
      } else if (newShift) {
        setNewShifts((prev) =>
          prev.map((s) => (s.guid === shift.guid ? { ...cleanShift } : s))
        );
      } else {
        setUpdatedShifts((prev) => {
          const filtered = prev.filter((s) => s.guid !== shift.guid);
          return [...filtered, cleanShift];
        });
      }
      setHasUnsavedChanges(true);
      setTimelineVersion((v) => v + 1);
      return true;
    },
    [
      calendarAssignment,
      employeesShifts,
      newShifts,
      updatedShifts,
      deletedShiftIds,
      simulatedDeletedShifts,
      simulatedUpdatedShifts,
      toast,
      baseDate
    ]
  );

  const handleDeleteShift = (guid: string) => {
    const isNewShift = newShifts.some((shift) => shift.guid === guid);
    if (isNewShift) {
      setNewShifts((prev) => prev.filter((shift) => shift.guid !== guid));
      setTimelineVersion((v) => v + 1);
    } else {
      setDeletedShiftIds((prev) => [...prev, guid]);
    }
    setHasUnsavedChanges(true);
  };

  const handleUpdateAbsence = (absence: PersonAbsence): boolean => {
    // 1. Обновляем фильтрацию для allAbsences
    const allAbsences = [
      ...Object.values(employeesAbsences).flatMap((dates) =>
        Object.values(dates).flat()
      ),
      ...newAbsences,
      ...updatedAbsences
    ].filter((a) => {
      // Для сохраненных отсутствий проверяем по id
      if (a.id) {
        return !deletedAbsenceKeys.some((k) => 'id' in k && k.id === a.id);
      }
      // Для временных - по guid
      return !deletedAbsenceKeys.some((k) => 'guid' in k && k.guid === a.guid);
    });

    if (isAbsenceOverlapping(absence, allAbsences)) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Отсутствие пересекается с другим отсутствием'
      });
      return false;
    }

    const allShifts: Shift[] = [
      ...(calendarAssignment?.employees.flatMap((emp) =>
        Object.entries(employeesShifts?.[emp.id.toString()] ?? {}).flatMap(
          ([date, shiftArray]) =>
            shiftArray.map(
              (s) =>
                ({
                  id: s.id,
                  guid: s.guid,
                  personId: emp.id,
                  date,
                  departmentId: s.departmentId,
                  workdayStart: s.workdayStart,
                  workdayDuration: s.workdayDuration,
                  breakDuration: s.break,
                  isTemplate: s.isTemplate
                }) as Shift
            )
        )
      ) ?? []),
      ...newShifts,
      ...updatedShifts,
      ...simulatedUpdatedShifts
    ].filter(
      (s) =>
        !deletedShiftIds.includes(s.guid) &&
        !simulatedDeletedShifts.includes(s.guid)
    );

    const overlappingShifts = getOverlappingShifts(absence, allShifts);

    const cleanAbsence = {
      ...absence,
      guid: absence.guid.startsWith('temp-') ? generateGUID() : absence.guid
    };

    // 2. Обновляем поиск существующего отсутствия
    const existingAbsence = allAbsences.find((a) => {
      // Для сохраненных отсутствий сравниваем по id
      if (a.id && cleanAbsence.id && a.id === cleanAbsence.id) return true;
      // Для временных - по guid
      if (a.guid && cleanAbsence.guid && a.guid === cleanAbsence.guid)
        return true;

      // Для новых записей сравниваем по основным полям
      return (
        a.personId === cleanAbsence.personId &&
        a.date === cleanAbsence.date &&
        a.from === cleanAbsence.from
      );
    });

    // Remove old simulations if updating
    if (existingAbsence) {
      const absGuid = cleanAbsence.guid;
      if (simulatedChangesByAbsence[absGuid]) {
        const { deleted, updated } = simulatedChangesByAbsence[absGuid];
        setSimulatedDeletedShifts((prev) =>
          prev.filter((g) => !deleted.includes(g))
        );
        setSimulatedUpdatedShifts((prev) =>
          prev.filter((s) => !updated.includes(s.guid))
        );
        setSimulatedChangesByAbsence((prev) => {
          const newPrev = { ...prev };
          delete newPrev[absGuid];
          return newPrev;
        });
      }
    }

    // Apply new simulations if shouldRemoveShift
    let changes = { deleted: [], updated: [] } as {
      deleted: string[];
      updated: string[];
    };
    if (absence.absence.shouldRemoveShift && overlappingShifts.length > 0) {
      const deleted: string[] = [];
      const updatedSh: Shift[] = [];
      const updatedG: string[] = [];
      overlappingShifts.forEach((shift) => {
        const shiftStart = moment(`${shift.date}T${shift.workdayStart}:00`);
        const shiftEnd = shiftStart
          .clone()
          .add(moment.duration(shift.workdayDuration));
        const absStart = moment(absence.from);
        const absEnd = moment(absence.to);
        const interStart = moment.max(shiftStart, absStart);
        const interEnd = moment.min(shiftEnd, absEnd);
        if (interEnd.isAfter(interStart)) {
          const interDur = moment.duration(interEnd.diff(interStart));
          const shiftDur = moment.duration(shift.workdayDuration);
          if (interDur.asMilliseconds() >= shiftDur.asMilliseconds()) {
            deleted.push(shift.guid);
          } else {
            const newStartM = shiftStart.clone().add(interDur);
            const newWorkdayStart = newStartM.format('HH:mm');
            const newDur = shiftDur.subtract(interDur);
            const newWorkdayDuration = `${String(
              Math.floor(newDur.asHours())
            ).padStart(2, '0')}:${String(newDur.minutes()).padStart(2, '0')}`;
            const upShift = {
              ...shift,
              workdayStart: newWorkdayStart,
              workdayDuration: newWorkdayDuration
            };
            updatedSh.push(upShift);
            updatedG.push(shift.guid);
          }
        }
      });

      setSimulatedDeletedShifts((prev) => {
        const newSet = new Set([...prev, ...deleted]);
        return Array.from(newSet);
      });
      setSimulatedUpdatedShifts((prev) => {
        const filtered = prev.filter((s) => !updatedG.includes(s.guid));
        return [...filtered, ...updatedSh];
      });
      changes = { deleted, updated: updatedG };
    }

    // Save changes by absence
    setSimulatedChangesByAbsence((prev) => ({
      ...prev,
      [cleanAbsence.guid]: changes
    }));

    // Save absence
    if (existingAbsence) {
      if (existingAbsence.id) {
        // Для сохраненных записей
        cleanAbsence.updated = true;
        setUpdatedAbsences((prev) => [
          ...prev.filter((a) => a.id !== cleanAbsence.id),
          cleanAbsence
        ]);
      } else {
        // Для временных записей
        setNewAbsences((prev) => [
          ...prev.filter((a) => a.guid !== cleanAbsence.guid),
          cleanAbsence
        ]);
      }
    } else {
      setNewAbsences((prev) => [...prev, cleanAbsence]);
    }

    setHasUnsavedChanges(true);
    return true;
  };

  const handleDeleteAbsence = (absence: PersonAbsence) => {
    const absGuid = absence.guid;
    if (simulatedChangesByAbsence[absGuid]) {
      const { deleted, updated } = simulatedChangesByAbsence[absGuid];
      setSimulatedDeletedShifts((prev) =>
        prev.filter((g) => !deleted.includes(g))
      );
      setSimulatedUpdatedShifts((prev) =>
        prev.filter((s) => !updated.includes(s.guid))
      );
      setSimulatedChangesByAbsence((prev) => {
        const newPrev = { ...prev };
        delete newPrev[absGuid];
        return newPrev;
      });
    }

    if (absence.id) {
      setDeletedAbsenceKeys((prev) => [...prev, { id: absence.id }]);
    } else if (absence.guid) {
      setNewAbsences((prev) => prev.filter((a) => a.guid !== absence.guid));
      setUpdatedAbsences((prev) => prev.filter((a) => a.guid !== absence.guid));
    }

    setHasUnsavedChanges(true);
    // setAbsenceDialogVisible(false);
  };

  const getAssignmentCallback = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      if (!selectedDepartmentFilter?.key) {
        setCalendarAssignment(null);
        setDepartmentSchedules(null);
        if (viewMode === 'timesheet') {
          setEmployeesTimesheet({});
        } else {
          setEmployeesShifts(null);
        }
        setLoading(false);
        return;
      }

      try {
        const departmentId = Number(selectedDepartmentFilter.key);
        const isTimesheet = viewMode === 'timesheet';

        const employeeSchedulesPromise = isTimesheet
          ? null
          : ScheduleDepartmentService.getPersonsSchedules(
              departmentId,
              dateForQuery
            );

        const [
          response,
          departmentSchedulesResponse,
          employeeSchedulesResponse
        ] = await Promise.all([
          ScheduleDepartmentService.getDiagram(departmentId, {
            ...apiParams,
            filterEmployeesByAvailableToday: showOnlyFreeEmployees,
            excludeLongAbsences: excludeLongAbsences,
            todayShiftOnly: todayShiftOnly
          }),
          ScheduleDepartmentService.getDepartmentsSchedule(
            departmentId,
            dateForQuery
          ),
          employeeSchedulesPromise,
          isTimesheet ? fetchTimesheet(signal) : null,
          fetchPersonAbsences(signal),
          fetchPersonTasks(signal)
        ]);

        if (signal?.aborted) return;

        setCalendarAssignment(response.data?.value || null);
        setDepartmentSchedules(departmentSchedulesResponse.data?.value || null);

        if (isTimesheet) {
          setEmployeesShifts(null);
        } else if (employeeSchedulesResponse) {
          const updatedEmployeeSchedules: EmployeesSchedules = {};
          Object.entries(employeeSchedulesResponse.data?.value || {}).forEach(
            ([personId, schedules]) => {
              updatedEmployeeSchedules[personId] = {};
              Object.entries(schedules).forEach(([date, scheduleArray]) => {
                updatedEmployeeSchedules[personId][date] = (
                  scheduleArray as DaySchedule[]
                ).map((schedule) => ({
                  ...schedule,
                  guid: schedule.guid || generateGUID()
                }));
              });
            }
          );

          setEmployeesShifts(updatedEmployeeSchedules);
          setEmployeesTimesheet({});
        }
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Ошибка загрузки данных для диаграммы'
        });
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [
      selectedDepartmentFilter,
      apiParams,
      dateForQuery,
      viewMode,
      showOnlyFreeEmployees,
      excludeLongAbsences,
      todayShiftOnly
    ]
  );

  const handleSaveChanges = async () => {
    try {
      const promises: Promise<any>[] = [];
      for (const shift of newShifts) {
        promises.push(
          SchedulePersonShiftService.createPersonShift(shift.personId, {
            ...shift
          })
        );
      }
      for (const shift of updatedShifts) {
        if (shift.id) {
          promises.push(
            SchedulePersonShiftService.updatePersonShift(shift.id, { ...shift })
          );
        }
      }
      for (const guid of deletedShiftIds) {
        const originalShift = getOriginalShiftData(guid);
        if (originalShift && originalShift.id) {
          promises.push(
            SchedulePersonShiftService.deletePersonShift(originalShift.id)
          );
        }
      }
      for (const absence of newAbsences) {
        promises.push(
          AbsenceService.createPersonAbsence(absence.personId, absence)
        );
      }
      for (const absence of updatedAbsences) {
        promises.push(
          AbsenceService.updatePersonAbsence(
            absence.personId,
            absence.date,
            absence
          )
        );
      }
      for (const key of deletedAbsenceKeys) {
        if ('id' in key) {
          promises.push(AbsenceService.deletePersonAbsence(key.id));
        }
      }
      await Promise.all(promises);
      await getAssignmentCallback();
      fetchPersonAbsences();
      setNewShifts([]);
      setUpdatedShifts([]);
      setDeletedShiftIds([]);
      setNewAbsences([]);
      setUpdatedAbsences([]);
      setDeletedAbsenceKeys([]);
      setSimulatedDeletedShifts([]);
      setSimulatedUpdatedShifts([]);
      setSimulatedChangesByAbsence({});
      setHasUnsavedChanges(false);
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Изменения сохранены',
        life: 3000
      });
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось сохранить изменения',
        life: 5000
      });
    }
  };

  useEffect(() => {
    if (
      newShifts.length > 0 ||
      updatedShifts.length > 0 ||
      deletedShiftIds.length > 0
    ) {
      setTimelineVersion((v) => v + 1);
    }
  }, [newShifts, updatedShifts, deletedShiftIds]);

  return {
    calendarAssignment,
    setCalendarAssignment,
    departmentSchedules,
    setDepartmentSchedules,
    employeesShifts,
    setEmployeesShifts,
    employeesAbsences,
    employeesTimesheet,
    fetchTimesheet,
    employeesTotalTimesheet,
    fetchTotalTimesheet,
    setEmployeesAbsences,
    employeesTasks,
    setEmployeesTasks,
    newAbsences,
    setNewAbsences,
    updatedAbsences,
    setUpdatedAbsences,
    deletedAbsenceKeys,
    setDeletedAbsenceKeys,
    newShifts,
    setNewShifts,
    updatedShifts,
    setUpdatedShifts,
    deletedShiftIds,
    setDeletedShiftIds,
    simulatedDeletedShifts,
    setSimulatedDeletedShifts,
    simulatedUpdatedShifts,
    setSimulatedUpdatedShifts,
    simulatedChangesByAbsence,
    setSimulatedChangesByAbsence,
    loading,
    setLoading,
    isDepartmentsLoaded,
    setIsDepartmentsLoaded,
    treeDropDownValue,
    setTreeDropDownValue,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleSaveShift,
    handleDeleteShift,
    handleUpdateAbsence,
    handleDeleteAbsence,
    handleSaveChanges,
    getAssignmentCallback,
    fetchPersonAbsences,
    getDepartmentsCallback
  };
};
